/**
 * Interior Designers — projektanci wnętrz (T17 2026-05-27).
 *
 * Osobna strona od /architects bo:
 *  - Architekci z GUNB to architekci BUDOWLANI (z pozwoleniami)
 *  - Projektanci wnętrz NIE są w GUNB (nie potrzebują pozwoleń)
 *  - To 2 zupełnie różne profesje + 2 różne cykle sprzedaży LED
 *
 * Źródła danych (planowane):
 *  - manual (dziś) — Miro/Iza dodają ręcznie
 *  - OWPP/SARP scrapers (T18 — następna iteracja)
 *  - Houzz Polska scraper (T19 — następna iteracja)
 *  - GUNB temporal cross-ref (już dziś — inwestycje permit_date+6-12m = "interior phase")
 *
 * Persona awareness:
 *  - seller_led: widzi listę jako "kogo można skontaktować dla LED interior"
 *  - designer: widzi siebie i kolegów po fachu (network)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Palette, Plus, MapPin, Globe, Mail, Phone as PhoneIcon, Instagram, Loader2,
  Search, Award, ExternalLink, Trash2, Edit3, Sparkles, Building2,
  Zap, Layers, Microscope, Brain,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  listInteriorDesigners, getInteriorDesignerStats, createInteriorDesigner,
  updateInteriorDesigner, deleteInteriorDesigner,
  sphinxEnrichDesigner, bulkExtractContacts,
} from '../lib/api';

const VOIVODESHIPS = [
  'Mazowieckie', 'Małopolskie', 'Dolnośląskie', 'Wielkopolskie', 'Pomorskie',
  'Śląskie', 'Łódzkie', 'Lubelskie', 'Podkarpackie', 'Świętokrzyskie',
  'Warmińsko-Mazurskie', 'Podlaskie', 'Kujawsko-Pomorskie', 'Zachodniopomorskie',
  'Lubuskie', 'Opolskie',
];

const SPECIALIZATIONS = [
  { key: 'residential',     label: 'Mieszkania / domy' },
  { key: 'commercial',      label: 'Lokale komercyjne' },
  { key: 'hotel',           label: 'Hotele / pensjonaty' },
  { key: 'restaurant',      label: 'Restauracje / kawiarnie' },
  { key: 'office',          label: 'Biura / coworking' },
  { key: 'retail',          label: 'Sklepy / butiki' },
  { key: 'showroom',        label: 'Showroomy' },
  { key: 'healthcare',      label: 'Obiekty medyczne' },
  { key: 'education',       label: 'Edukacja / przedszkola' },
  { key: 'home_staging',    label: 'Home staging' },
];

const STYLES = [
  { key: 'modern',      label: 'Nowoczesny' },
  { key: 'scandi',      label: 'Skandynawski' },
  { key: 'minimalist',  label: 'Minimalistyczny' },
  { key: 'industrial',  label: 'Industrialny' },
  { key: 'classic',     label: 'Klasyczny' },
  { key: 'bohemian',    label: 'Bohemian' },
  { key: 'japandi',     label: 'Japandi' },
  { key: 'mid_century', label: 'Mid-century' },
  { key: 'loft',        label: 'Loft' },
  { key: 'glamour',     label: 'Glamour' },
];

const SOURCE_LABELS = {
  manual:            'Ręcznie',
  serper_discovery:  '🔍 Google (Serper)',
  sphinx_discovery:  '🔍 Google (legacy)',
  houzz:             'Houzz',
  sarp:              'SARP',
  owpp:              'OWPP',
  iarp_ref:          'IARP (ref)',
  instagram:         'Instagram',
  behance:           'Behance',
  homebook:          'Homebook',
  press:             'Prasa',
};

function DesignerCard({ designer, onEdit, onDelete }) {
  return (
    <Card className="glass-card lift-hover border-fuchsia-500/20 hover:border-fuchsia-500/40">
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Palette className="w-4 h-4 text-primary flex-shrink-0" />
              <h3 className="text-sm font-semibold truncate">{designer.name}</h3>
              {designer.studio_name && (
                <Badge variant="outline" className="text-[10px] py-0">{designer.studio_name}</Badge>
              )}
              {designer.is_verified && (
                <Badge className="text-[10px] py-0 bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                  ✓ verified
                </Badge>
              )}
              {designer.is_seed_placeholder && (
                <Badge variant="outline" className="text-[10px] py-0 border-amber-500/30 text-amber-400">
                  placeholder
                </Badge>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-1 text-xs text-muted-foreground">
              {designer.city && (
                <span className="flex items-center gap-0.5">
                  <MapPin className="w-3 h-3" /> {designer.city}
                  {designer.voivodeship ? `, ${designer.voivodeship}` : ''}
                </span>
              )}
              {designer.projects_count > 0 && (
                <span>{designer.projects_count} realizacji</span>
              )}
              {designer.source && (
                <Badge variant="outline" className="text-[10px] py-0 h-4">
                  {SOURCE_LABELS[designer.source] || designer.source}
                </Badge>
              )}
            </div>

            {/* Specializations + styles */}
            <div className="flex flex-wrap gap-1 mt-2">
              {(designer.specializations || []).slice(0, 5).map(s => (
                <Badge key={s} variant="secondary" className="text-[10px] py-0">
                  {SPECIALIZATIONS.find(sp => sp.key === s)?.label || s}
                </Badge>
              ))}
              {(designer.styles || []).slice(0, 3).map(s => (
                <Badge key={s} variant="outline" className="text-[10px] py-0">
                  {STYLES.find(st => st.key === s)?.label || s}
                </Badge>
              ))}
            </div>

            {/* Contact + links */}
            <div className="flex flex-wrap gap-2 mt-2 text-xs">
              {designer.email && (
                <a href={`mailto:${designer.email}`}
                   className="inline-flex items-center gap-1 text-primary hover:underline">
                  <Mail className="w-3 h-3" /> {designer.email}
                </a>
              )}
              {designer.phone && (
                <a href={`tel:${designer.phone}`}
                   className="inline-flex items-center gap-1 text-primary hover:underline">
                  <PhoneIcon className="w-3 h-3" /> {designer.phone}
                </a>
              )}
              {designer.website && (
                <a href={designer.website} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <Globe className="w-3 h-3" /> Strona
                </a>
              )}
              {designer.instagram && (
                <a href={`https://instagram.com/${designer.instagram.replace('@', '')}`}
                   target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <Instagram className="w-3 h-3" /> {designer.instagram}
                </a>
              )}
              {designer.houzz_url && (
                <a href={designer.houzz_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                  <ExternalLink className="w-3 h-3" /> Houzz
                </a>
              )}
            </div>

            {/* Awards */}
            {(designer.awards || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {(designer.awards || []).slice(0, 3).map((a, i) => (
                  <Badge key={i} variant="outline" className="text-[10px] py-0 border-amber-500/30 text-amber-400">
                    <Award className="w-2.5 h-2.5 mr-0.5" /> {a.name} {a.year ? `(${a.year})` : ''}
                  </Badge>
                ))}
              </div>
            )}

            {designer.notes && (
              <div className="text-xs text-muted-foreground mt-2 p-2 rounded bg-muted/30 italic">
                {designer.notes}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={() => onEdit(designer)} className="h-7 px-2"
                    title="Edytuj">
              <Edit3 className="w-3 h-3" />
            </Button>
            {/* T26 — SPHINX depth dropdown (quick/full/deep/auto) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 px-2"
                  title={designer.sphinx_enriched
                    ? `Re-enrich SPHINX (ostatnio: ${(designer.sphinx_enriched_at || '').slice(0, 10)})`
                    : 'Wzbogać profil przez SPHINX — wybierz głębokość'}>
                  <Sparkles className={`w-3 h-3 ${designer.sphinx_enriched ? 'text-amber-400' : ''}`} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-xs">SPHINX głębokość</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {[
                  { depth: 'quick', icon: Zap, color: 'text-emerald-400', label: 'Szybki',
                    desc: '~10s · tylko KRS/CEIDG · tani' },
                  { depth: 'full', icon: Layers, color: 'text-amber-400', label: 'Pełen (default)',
                    desc: '~90s · social + wayback + DNS + reviews' },
                  { depth: 'deep', icon: Microscope, color: 'text-red-400', label: 'Głęboki',
                    desc: '~150s · + CRBR + financial + sanctions · drogi' },
                  { depth: 'auto', icon: Brain, color: 'text-purple-400', label: 'Auto (adaptive)',
                    desc: 'Start quick, escalates gdy risk wykryty' },
                ].map(opt => {
                  const Icon = opt.icon;
                  return (
                    <DropdownMenuItem key={opt.depth} onClick={async () => {
                      const labelMap = { quick: '~10s', full: '~90s', deep: '~150s', auto: '~30-150s' };
                      toast.info(`SPHINX ${opt.label.toLowerCase()} dla ${designer.name}... (${labelMap[opt.depth]})`);
                      try {
                        const r = await sphinxEnrichDesigner(designer.id, opt.depth);
                        const endpointsTxt = r.queries?.map(q => q.endpoint).join(', ') || '—';
                        const escalatedTxt = r.escalated ? ` · ⚠ ${r.escalated}` : '';
                        if (r.errors?.length) {
                          toast.warning(`Wzbogacono z ostrzeżeniami (${endpointsTxt}): ${r.errors[0]}${escalatedTxt}`);
                        } else {
                          toast.success(
                            `✨ ${opt.label} done (${endpointsTxt})${escalatedTxt} · auto-fill: ${r.auto_filled?.length ? r.auto_filled.join(', ') : 'brak'}`
                          );
                        }
                        if (typeof window !== 'undefined') {
                          setTimeout(() => window.location.reload(), 1500);
                        }
                      } catch (e) {
                        toast.error(`SPHINX ${opt.depth} nie powiódł się: ` + (e.response?.data?.detail || e.message));
                      }
                    }}>
                      <Icon className={`w-3.5 h-3.5 mr-2 ${opt.color}`} />
                      <div className="flex-1">
                        <div className="text-xs font-medium">{opt.label}</div>
                        <div className="text-[10px] text-muted-foreground">{opt.desc}</div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button variant="ghost" size="sm" onClick={() => onDelete(designer)}
                    className="h-7 px-2 text-destructive hover:text-destructive"
                    title="Usuń">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


function DesignerForm({ designer, onSave, onCancel }) {
  const [form, setForm] = useState(() => ({
    name: '', studio_name: '', email: '', phone: '', website: '',
    city: '', voivodeship: '',
    specializations: [], styles: [],
    instagram: '', behance: '', linkedin: '', houzz_url: '',
    source: 'manual',
    source_url: '',
    notes: '',
    ...(designer || {}),
  }));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || form.name.trim().length < 2) {
      toast.error('Imię/nazwa jest wymagane');
      return;
    }
    setSaving(true);
    try {
      const cleaned = {
        ...form,
        name: form.name.trim(),
        studio_name: (form.studio_name || '').trim() || undefined,
        email: (form.email || '').trim() || undefined,
        phone: (form.phone || '').trim() || undefined,
        website: (form.website || '').trim() || undefined,
        instagram: (form.instagram || '').trim() || undefined,
      };
      await onSave(cleaned);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (field, key) => {
    setForm(f => ({
      ...f,
      [field]: f[field].includes(key) ? f[field].filter(k => k !== key) : [...f[field], key],
    }));
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Imię i nazwisko *</Label>
          <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                 placeholder="np. Anna Nowak" />
        </div>
        <div>
          <Label className="text-xs">Nazwa studia</Label>
          <Input value={form.studio_name} onChange={e => setForm({ ...form, studio_name: e.target.value })}
                 placeholder="np. Studio Wnętrz Hola" />
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Telefon</Label>
          <Input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Strona www</Label>
          <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                 placeholder="https://..." />
        </div>
        <div>
          <Label className="text-xs">Instagram</Label>
          <Input value={form.instagram} onChange={e => setForm({ ...form, instagram: e.target.value })}
                 placeholder="@studio_xyz" />
        </div>
        <div>
          <Label className="text-xs">Miasto</Label>
          <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
        </div>
        <div>
          <Label className="text-xs">Województwo</Label>
          <Select value={form.voivodeship || ''} onValueChange={(v) => setForm({ ...form, voivodeship: v })}>
            <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {VOIVODESHIPS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs">Specjalizacje</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {SPECIALIZATIONS.map(s => (
            <button key={s.key} type="button"
                    onClick={() => toggle('specializations', s.key)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                      form.specializations.includes(s.key)
                        ? 'bg-primary/15 text-primary border-primary/40'
                        : 'border-border hover:border-muted-foreground'
                    }`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs">Style</Label>
        <div className="flex flex-wrap gap-1 mt-1">
          {STYLES.map(s => (
            <button key={s.key} type="button"
                    onClick={() => toggle('styles', s.key)}
                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                      form.styles.includes(s.key)
                        ? 'bg-primary/15 text-primary border-primary/40'
                        : 'border-border hover:border-muted-foreground'
                    }`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs">Notatki</Label>
        <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                  placeholder="np. Polecony przez X, specjalizuje się w hotelach butikowych" />
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <Button variant="ghost" onClick={onCancel}>Anuluj</Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
          Zapisz
        </Button>
      </div>
    </div>
  );
}


export default function InteriorDesigners({ lang }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: '', city: '', specialization: 'all', source: 'all', voivodeship: 'all', country: 'PL', has_contacts: false });
  const [sortBy, setSortBy] = useState('region');  // T57 — domyślnie po regionach
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDesigner, setEditingDesigner] = useState(null);
  // T48 2026-05-30 — paginacja (baza urosła do 1000+; lista pokazywała tylko 50)
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 60;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy, page, page_size: PAGE_SIZE };
      if (filters.q.trim()) params.q = filters.q.trim();
      if (filters.city.trim()) params.city = filters.city.trim();
      if (filters.specialization !== 'all') params.specialization = filters.specialization;
      if (filters.source !== 'all') params.source = filters.source;
      if (filters.voivodeship !== 'all') params.voivodeship = filters.voivodeship;
      params.country = filters.country;
      if (filters.has_contacts) params.has_contacts = true;
      const r = await listInteriorDesigners(params);
      setItems(r.items || []);
      setTotal(r.total || 0);
    } catch (e) {
      toast.error('Błąd ładowania: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filters, page, sortBy]);

  // Reset do strony 1 gdy zmienią się filtry / sortowanie
  useEffect(() => { setPage(1); }, [filters, sortBy]);

  const loadStats = useCallback(async () => {
    try {
      const s = await getInteriorDesignerStats();
      setStats(s);
    } catch (e) { /* silent */ }
  }, []);

  // T46 2026-05-30 — reużywalne discovery (Serper Google). Wołane z przycisku
  // i z empty-state CTA gdy SZUKAJ (filtr lokalny) nic nie znajdzie.
  const runDiscovery = useCallback(async (city) => {
    if (!city || !city.trim()) return;
    if (!window.confirm(`Wyszukać studia projektowania wnętrz w "${city}" przez Google (Serper)? ~10s. Nowe trafią do bazy jako weryfikowalne (potem SPHINX enrich + scrape kontaktów).`)) return;
    try {
      toast.info(`Discovery dla ${city}... (~10s)`);
      const r = await fetch('/api/interior-designers/discover-from-sphinx?' + new URLSearchParams({ city: city.trim() }), {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('access_token') },
      }).then(r => r.json());
      if (r.discovered) {
        toast.success(`✨ Znaleziono ${r.discovered}, dodano ${r.inserted}${r.skipped_dedup ? ` (pominięto ${r.skipped_dedup} duplikatów)` : ''}.`);
        load(); loadStats();
      } else {
        toast.warning(r.message || `Brak nowych wyników dla ${city}.`);
      }
    } catch (e) {
      toast.error('Discovery nie powiódł się: ' + (e.response?.data?.detail || e.message));
    }
  }, [load, loadStats]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleSave = async (data) => {
    try {
      if (editingDesigner?.id) {
        await updateInteriorDesigner(editingDesigner.id, data);
        toast.success('Zaktualizowano');
      } else {
        await createInteriorDesigner(data);
        toast.success('Dodano nowego projektanta');
      }
      setDialogOpen(false);
      setEditingDesigner(null);
      load();
      loadStats();
    } catch (e) {
      toast.error('Zapis nie powiódł się: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (designer) => {
    if (!window.confirm(`Usunąć ${designer.name}? Akcja nieodwracalna.`)) return;
    try {
      await deleteInteriorDesigner(designer.id);
      toast.success('Usunięto');
      load();
      loadStats();
    } catch (e) {
      toast.error('Usunięcie nie powiodło się: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Palette className="w-6 h-6 text-primary" />
            Projektanci wnętrz
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Osobna kategoria od architektów budowlanych. Projektanci wnętrz zajmują się fazą
            wykończeniową — kluczowy moment dla zlecenia oświetlenia.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => { setEditingDesigner(null); setDialogOpen(true); }} className="gap-1.5">
            <Plus className="w-4 h-4" /> Dodaj projektanta
          </Button>
          {/* Discovery przez Serper Google — REAL data, nie hardcoded */}
          <Button variant="outline" onClick={() => {
            const city = window.prompt('Discovery (Serper.dev Google search) — wpisz miasto:', filters.city || '');
            runDiscovery(city);
          }} className="gap-1.5">
            🔍 Discovery (Google + SPHINX)
          </Button>
          {/* T40 — bulk contact extraction ze stron www */}
          <Button variant="outline" onClick={async () => {
            if (!window.confirm('Wyciągnąć email/telefon ze stron www wszystkich projektantów bez kontaktu? MASKIL scrapuje ich stronę /kontakt. ~1-2 min.')) return;
            try {
              toast.info('Wyciągam kontakty ze stron www... (~1-2 min)');
              const r = await bulkExtractContacts(50, true);
              toast.success(`📞 Z ${r.scanned} stron: ${r.found_email} email, ${r.found_phone} telefon (${r.updated} zaktualizowanych)`);
              load(); loadStats();
            } catch (e) {
              toast.error('Extraction nie powiódł się: ' + (e.response?.data?.detail || e.message));
            }
          }} className="gap-1.5">
            📞 Wyciągnij kontakty
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Łącznie</div>
              <div className="text-xl font-bold">{stats.total}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Zweryfikowani</div>
              <div className="text-xl font-bold text-emerald-400">{stats.verified}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Aktywni</div>
              <div className="text-xl font-bold">{stats.active}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Top miasto</div>
              <div className="text-sm font-medium">
                {(stats.by_city?.[0]?._id) || '—'}{' '}
                <span className="text-muted-foreground text-xs">
                  ({stats.by_city?.[0]?.n || 0})
                </span>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-xs text-muted-foreground">Top spec</div>
              <div className="text-sm font-medium">
                {SPECIALIZATIONS.find(s => s.key === stats.by_specialization?.[0]?._id)?.label
                  || stats.by_specialization?.[0]?._id || '—'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-3 pb-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Szukaj</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={filters.q} onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                  placeholder="Imię, studio, miasto, email…"
                  className="pl-7"
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Miasto</Label>
              <Input value={filters.city} onChange={e => setFilters(f => ({ ...f, city: e.target.value }))}
                     placeholder="np. Warszawa" />
            </div>
            <div className="min-w-[160px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Specjalizacja</Label>
              <Select value={filters.specialization}
                      onValueChange={v => setFilters(f => ({ ...f, specialization: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  {SPECIALIZATIONS.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Źródło</Label>
              <Select value={filters.source} onValueChange={v => setFilters(f => ({ ...f, source: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie</SelectItem>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) =>
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[160px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Województwo</Label>
              <Select value={filters.voivodeship} onValueChange={v => setFilters(f => ({ ...f, voivodeship: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Wszystkie województwa</SelectItem>
                  {VOIVODESHIPS.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[140px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Kraj</Label>
              <Select value={filters.country} onValueChange={v => setFilters(f => ({ ...f, country: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PL">Polska</SelectItem>
                  <SelectItem value="all">Wszystkie kraje</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col justify-end min-w-[130px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Kontakt</Label>
              <label className="flex items-center gap-2 h-9 cursor-pointer select-none text-sm">
                <input
                  type="checkbox"
                  checked={filters.has_contacts}
                  onChange={e => setFilters(f => ({ ...f, has_contacts: e.target.checked }))}
                  className="w-4 h-4 accent-primary cursor-pointer"
                />
                Tylko z kontaktem
              </label>
            </div>
            {/* T57 — sortowanie A: regiony, B: branże */}
            <div className="min-w-[150px]">
              <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Sortuj</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="region">🗺️ Regionami</SelectItem>
                  <SelectItem value="specialization">🏷️ Branżami</SelectItem>
                  <SelectItem value="name">Nazwą (A-Z)</SelectItem>
                  <SelectItem value="discovered_at">Najnowsze</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
            {(filters.q || filters.city) ? (
              <>
                {/* T46 — filtr lokalny nic nie znalazł. Wyjaśnij, że SZUKAJ filtruje
                    istniejącą bazę, i zaproponuj prawdziwy crawl internetu. */}
                <h3 className="text-lg font-semibold mb-1">
                  Brak w bazie: „{filters.q || filters.city}"
                </h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  Pole <b>Szukaj</b> filtruje projektantów już zebranych w bazie (nazwa/miasto/email),
                  nie przeszukuje internetu. Adam grzebie autonomicznie co ~3h, ale możesz
                  uruchomić Discovery teraz dla konkretnego miasta.
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button onClick={() => runDiscovery(filters.city || filters.q)} className="gap-1.5">
                    🔍 Szukaj w internecie ({filters.city || filters.q})
                  </Button>
                  <Button variant="outline" onClick={() => setFilters(f => ({ ...f, q: '', city: '', specialization: 'all', source: 'all', voivodeship: 'all', country: 'PL', has_contacts: false }))} className="gap-1.5">
                    Wyczyść filtry
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold mb-1">Brak projektantów</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                  Adam zbiera projektantów autonomicznie (Serper Google, rotacja 25 miast × warianty fraz, co ~3h).
                  Możesz też dodać ręcznie albo uruchomić Discovery dla wybranego miasta.
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button onClick={() => { setEditingDesigner(null); setDialogOpen(true); }} className="gap-1.5">
                    <Plus className="w-4 h-4" /> Dodaj pierwszego
                  </Button>
                  <Button variant="outline" onClick={() => runDiscovery(window.prompt('Discovery — wpisz miasto:', 'Warszawa'))} className="gap-1.5">
                    🔍 Discovery (Google)
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map(d => (
            <DesignerCard key={d.id} designer={d}
                          onEdit={(des) => { setEditingDesigner(des); setDialogOpen(true); }}
                          onDelete={handleDelete} />
          ))}
        </div>
        {/* T48 — paginacja */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3 mt-4">
            <Button variant="outline" size="sm" disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}>
              ← Poprzednia
            </Button>
            <span className="text-sm text-muted-foreground">
              Strona {page} z {Math.ceil(total / PAGE_SIZE)} · {total} projektantów
            </span>
            <Button variant="outline" size="sm" disabled={page >= Math.ceil(total / PAGE_SIZE)}
                    onClick={() => setPage(p => p + 1)}>
              Następna →
            </Button>
          </div>
        )}
        </>
      )}

      {/* Add/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-primary" />
              {editingDesigner ? `Edytuj: ${editingDesigner.name}` : 'Dodaj projektanta wnętrz'}
            </DialogTitle>
          </DialogHeader>
          <DesignerForm
            designer={editingDesigner}
            onSave={handleSave}
            onCancel={() => { setDialogOpen(false); setEditingDesigner(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
